import { useEffect, useState } from "react";
import { IVoucher } from "../../models/Voucher";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { TextInput } from "../shared/Inputs/TextInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";

import { useVouchers } from "../../data/useVouchers";
import useSingleVoucher from "../../data/useSingleVoucher";
import useSuppliers from "../../data/useSuppliers";
import { useForm } from "react-hook-form";
import { ISupplier } from "../../models/Supplier";
import { prefix } from "@fortawesome/free-solid-svg-icons";
import GeneralPriceSettingSingle from "../priceSettings/PriceConfiguration/GeneralPriceSettingSingle";
import SelectInput from "../shared/Inputs/SelectInput";
import { set } from "lodash";

type FormValues = {
    name: string;
    description: string;

    code: string;

    supplierId: string;

    // TODO: Add price setting here
    priceSetting: {
        value: number;
        isFixed: boolean; // "true" | "false"
    };
};

export default function AddEditVoucher({ voucher, setOpen, supplier }: { voucher?: IVoucher; setOpen: any; supplier?: ISupplier }) {
    const { deleteHandlers, saveHandlers } = useDefaultCRUDHandlers("Voucher");
    const { create } = useVouchers();
    const { update, remove } = useSingleVoucher(voucher?.id ?? "");
    const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
    const { register, handleSubmit, reset, watch, setValue } = useForm<FormValues>();
    const { suppliers } = useSuppliers();
    const [supplierOptions, setSupplierOptions] = useState<{ id: string; name: string }[]>([]);
    
    const onSubmit = (data: any) => {
        console.log("Form Data", data);

        const updatedVoucher: IVoucher = {
            ...voucher,
            ...data,
            supplierId: data.supplierId ?? voucher?.supplierId ?? supplier?.id ?? null,
            priceSetting: {
                ...data.priceSetting,
                value:
                  data.priceSetting.value !== "" && data.priceSetting.value !== null
                    ? parseFloat(data.priceSetting.value)
                    : 0,
                isFixed: data.priceSetting.isFixed === true ? true : false,
            }
        };

        if (voucher !== undefined) {
            update.mutate(updatedVoucher, {
                onSuccess: (data) => {
                    saveHandlers.onSuccess();
                    setOpen(false);
                    reset({});
                },
                onError: saveHandlers.onError
            });
        } else {
            create.mutate(updatedVoucher, {
                onSuccess: (data) => {
                    saveHandlers.onSuccess();
                    setOpen(false);
                    reset({});
                },
                onError: saveHandlers.onError
            });
        }
    };

    const handleDelete = () => {
        remove.mutate(voucher?.id!, {
            onSuccess: (data) => {
                deleteHandlers.onSuccess();
                setOpen(false);
                reset({});
            },
            onError: deleteHandlers.onError
        });
    };

    const generateCode = (prefix: string) => {
        const randomString = Math.random().toString(36).substring(2, 8).toUpperCase();
        return `${prefix}-${randomString}`;
    }


    useEffect(() => {
        if (voucher === undefined) return; 
        
        reset({
            name: voucher?.name ?? "",
            description: voucher?.description ?? "",
            code: voucher?.code ?? generateCode("VOUCHER"),
            supplierId: voucher?.supplierId ?? supplier?.id ?? undefined,

            priceSetting: {
                value: voucher?.priceSetting?.value ?? 0,
                isFixed: voucher?.priceSetting?.isFixed ? true : false
            }
            
        });
    }, [voucher])

    useEffect(() => {
        if (supplier !== undefined) {
            // reset({ ...voucher, supplierId: supplier?.id ?? null });

            // take first character of each word in supplier name
            const supplierCode = supplier.name
                .split(" ")
                .map(word => word.charAt(0).toUpperCase())
                .join("");
            setValue("code", generateCode(supplierCode));
            setValue("supplierId", supplier.id ?? null);
        } else {
            // reset({ name: "", description: "", code: generateCode("VOUCHER"), supplierId: supplier?.id ?? null });
        }
    }, [supplier])

    useEffect(() => {
        if (suppliers.isLoading || !suppliers.data) return;

        const options = suppliers.data.data.map((supplier: ISupplier) => ({
            id: supplier.id,
            name: supplier.name
        }));

        console.log("Supplier Options", options);

        setSupplierOptions(options);
    }, [suppliers.isLoading, suppliers.data]);

    return (
        <>
            <h1 className="text-xl">{`${voucher ? "Edit" : "Add"} a Voucher`}</h1>
            <small className="text-gray-500"><i>
                {voucher ? "Edit the voucher details" : "Add a new voucher"}
            </i></small>

            <form className="" onSubmit={handleSubmit(onSubmit)}>
                <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <TextInput
                        label="Name"
                        placeholder="Enter voucher name"
                        registerName={"name"}
                        register={register}
                    />
                    <TextInput
                        label="Code"
                        placeholder="Enter voucher code"
                        registerName={"code"}
                        register={register}
                    />
                    <div className="col-span-1 sm:col-span-2">
                        <TextInput
                            label="Description"
                            placeholder="Enter voucher description"
                            richText={true}
                            registerName={"description"}
                            register={register}
                        />
                    </div>
                    <SelectInput
                        label="Supplier"
                        register={register}
                        registerName={"supplierId"}
                        options={supplierOptions}
                        setValue={setValue}
                        watch={watch}
                    />
                    <div className="mt-[-9px]">
                        <GeneralPriceSettingSingle
                            label={"Price Change"}
                            objectName={"priceSetting"}
                            register={register}
                            setValue={setValue}
                            watch={watch}
                            minVal={null} 
                        />
                    </div>
                </div>

                <ButtonInput isSubmit={true} label={voucher ? "Update Voucher" : "Create Voucher"} classes="float-right" />
                
                {voucher && (
                    <>
                        <ButtonInput isSubmit={false} label="Delete Voucher" onClick={() => setDeleteOpen(true)} classes="bg-red-600 hover:bg-red-700" />
                        <ConfirmModal
                            open={deleteOpen}
                            setOpen={setDeleteOpen}
                            title="Delete Voucher"
                            message={`Are you sure you want to delete this voucher?`}
                            confirmButtonText="Delete"
                            onConfirm={handleDelete}
                            isLoading={remove.isLoading}
                        />
                    </>
                )}
            </form>
        </>
    );
}